/**
 * useBadgeAnnounceWatcher — o ANÚNCIO de conquista deixa de depender de quem
 * concedeu (BUG-281).
 *
 * Até 21/09 o cartão de conquista só abria quando o próprio client pedia
 * `unlockBadge` e recebia `{unlocked: true}`. Com a concessão migrada para o
 * servidor (`onUserStatsWritten`), `users.badges` passa a crescer sem nenhum
 * pedido do client — e a skill já registrava que "badge concedida pelo
 * servidor é MUDA". Este hook é o anunciante que faltava: observa
 * `profile.badges` direto e celebra o que ainda não está em
 * `profile.badgesAnnounced`, a baseline durável por conta.
 *
 * Regras:
 *  · `badges − badgesAnnounced − (liga/temporada/foco_maratona: `hasOwnBadgeAnnouncer`; NUNCA "concedida pelo servidor", BUG-303)` → fila de
 *    celebração (`enqueueBadgeUnlocks`, que já deduplica contra a fila e o
 *    `celebrated` local — se `useBadgeAutoUnlock` celebrou primeiro no mesmo
 *    tick, este enqueue é no-op);
 *  · depois de enfileirar, `badgesAnnounced` converge para `badges` INTEIRO
 *    por `arrayUnion` — inclui as de liga/temporada (anunciadas pelo cartão
 *    de fechamento, nunca aqui) e o que o caminho ativo já celebrou;
 *  · `badgesAnnounced` ausente = conta anterior ao campo → celebra tudo que
 *    está em `badges` na primeira carga. Decisão deliberada: o backfill de
 *    21/09 (425 badges em 145 contas) nunca foi anunciado, e ≥4 já vira
 *    "resumo" no `BadgeUnlockModal`.
 *
 * Marca por uid e por conjunto: o mesmo `badges` não gera dois writes na
 * mesma sessão, e a falha de rede não perde a celebração (já enfileirada) —
 * só o marcador cross-device espera a próxima mudança.
 */
import { useEffect, useRef } from "react";
import { useAuth } from "../../../hooks/useAuth";
import { enqueueBadgeUnlocks } from "../utils/badgeUnlockQueue";
import { hasOwnBadgeAnnouncer, BADGE_CATALOG } from "../services/gamificationService";
import { markBadgesAnnounced } from "../services/badgeAnnounceService";

/** Decisão pura: o que celebrar e o que marcar, dado o perfil. */
export function planBadgeAnnounce(
  badges: readonly string[] | undefined,
  badgesAnnounced: readonly string[] | undefined,
): { toCelebrate: string[]; toMark: string[] } {
  const known = (badges ?? []).filter((id) => !!BADGE_CATALOG[id]);
  const announced = new Set(badgesAnnounced ?? []);
  const toMark = known.filter((id) => !announced.has(id));
  const toCelebrate = toMark.filter((id) => !hasOwnBadgeAnnouncer(id));
  return { toCelebrate, toMark };
}

export function useBadgeAnnounceWatcher() {
  const { user, profile } = useAuth();
  /** `uid|ids ordenados` do último conjunto marcado — evita repetir o write. */
  const markedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!user || !profile) return;
    const { toCelebrate, toMark } = planBadgeAnnounce(profile.badges, profile.badgesAnnounced);
    if (toMark.length === 0) return;

    const key = `${user.uid}|${[...toMark].sort().join(",")}`;
    if (markedRef.current === key) return;
    markedRef.current = key;

    if (toCelebrate.length > 0) enqueueBadgeUnlocks(toCelebrate);

    void markBadgesAnnounced(user.uid, toMark).catch((err) => {
      // A celebração já está na fila; só o marcador fica para a próxima
      // mudança de `badges` (ou o próximo boot).
      console.warn("[Lura] badgesAnnounced não sincronizou:", err);
      if (markedRef.current === key) markedRef.current = null;
    });
  }, [user, profile]);
}
